import { useCallback, useRef, useState } from 'react'
import { contentful } from 'services/contentful'
import { log } from 'services/util/log'

const useArticles = ({
  articles,
  contentfulQuery,
  filterIds = [], // array of ids to exclude from query
  perPage = 9,
}) => {
  const [articlesMap, setArticlesMap] = useState(
    // the articles are stored in a Map where [k, v] = [a.id, a]
    new Map(articles.map(a => [a.id, a]))
  )

  const [hasMore, setHasMore] = useState(true)

  const articlesArray = useRef(Array.from(articlesMap.values()))

  // useCallback gives this function stable identity unless the dependencies change
  const fetchArticles = useCallback(
    async (numToFetch = perPage) => {
      // join all the excluded ids with commas—this goes to contentful
      const exclude = filterIds.join(',')
      try {
        const query = {
          ...contentfulQuery,
          limit: numToFetch,
          skip: articlesMap.size,
          'sys.id[nin]': exclude || null,
        }
        const result = await contentful.getArticles(query)
        if (result.articles.length) {
          // successful request with query result
          let changed = false
          for (const a of result.articles) {
            if (!articlesMap.has(a.id)) {
              const formatted = a
              articlesMap.set(formatted.id, formatted)
              articlesArray.current.push(formatted)
              changed = true
            }
          }
          // setState uses reference identity (via Object.is()) to decide
          // whether to update state, so to update the map, we have to have a
          // new address for it.  if we haven't changed anything, we'll bail
          // out of the state update by returning the same object as before
          setArticlesMap(changed ? new Map(articlesMap) : articlesMap)
        } else if (!result.articles.length) {
          setHasMore(false)
        } else if (result.error) {
          // successful request with bad query
          log('bad contentful query in useArticles:', 'error', { result, query })
          // noop
          setArticlesMap(articlesMap)
        }
      } catch (err) {
        // request failed
        log('fetching articles failed in useArticles', 'error', {
          error: err,
          articleIds: [...articlesMap.keys()],
          query: contentfulQuery,
          filterIds,
          perPage,
        })
        setArticlesMap(articlesMap)
      }
    },
    [contentfulQuery, filterIds, perPage]
  )

  return {
    articles: articlesArray.current,
    articlesMap,
    fetchArticles,
    hasMore,
  }
}

export default useArticles
